import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { notify } from "../../../../assets/helpers/toast";
import { servScalePriceHistoryTableHeader } from "../../../../assets/helpers/tables";
import { servScalePriceService } from "../../../../services/servScalePriceService";
import {
  SERV_SCALE_FIELD_LABELS,
  avatarBgForName,
  formatHistoryValue,
  servScaleHistoryFieldKeys,
  userDisplayName,
  userInitials,
} from "../../../common/RhfParameterInputs/rhfParameterInputsShared.js";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function buildPageWindows(page, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const core = new Set(
    [1, totalPages, page, page - 1, page + 1].filter((p) => p >= 1 && p <= totalPages)
  );
  const sorted = [...core].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("…");
    result.push(sorted[i]);
  }
  return result;
}

function HistoryPager({ isFooter, total, page, totalPages, onPrev, onNext, onGoTo }) {
  const pages = buildPageWindows(page, totalPages);
  return (
    <div
      className={
        isFooter
          ? "baremo-history__toolbar baremo-history__toolbar--footer"
          : "baremo-history__toolbar"
      }
      role="navigation"
      aria-label={
        isFooter ? "Paginación del historial (pie)" : "Paginación del historial"
      }
    >
      {!isFooter && (
        <span className="baremo-history__toolbar-summary">
          {total} {total === 1 ? "evento" : "eventos"}
        </span>
      )}
      <div className="baremo-history__pager-actions">
        <button
          type="button"
          className="baremo-history__page-btn"
          disabled={page <= 1 || total === 0}
          onClick={onPrev}
          aria-label="Página anterior"
        >
          ‹
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="baremo-history__page-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`baremo-history__page-num ${p === page ? "baremo-history__page-num--active" : ""}`}
              onClick={() => onGoTo(p)}
              aria-current={p === page ? "page" : undefined}
              disabled={total === 0}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="baremo-history__page-btn"
          disabled={page >= totalPages || total === 0}
          onClick={onNext}
          aria-label="Página siguiente"
        >
          ›
        </button>
      </div>
    </div>
  );
}

export const ServScalePriceHistorySection = () => {
  const [fieldFilter, setFieldFilter] = useState("");
  const [dateSinceInit, setDateSinceInit] = useState(() =>
    moment().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [dateSinceEnd, setDateSinceEnd] = useState(() =>
    moment().add(1, "month").format("YYYY-MM-DD")
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const goPrev = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const goNext = useCallback(
    () => setPage((p) => Math.min(totalPages, p + 1)),
    [totalPages]
  );
  const goTo = useCallback(
    (n) => setPage(Math.min(Math.max(1, n), totalPages)),
    [totalPages]
  );

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const toDate = (ymd) =>
        ymd && moment(ymd, "YYYY-MM-DD", true).isValid()
          ? moment(ymd, "YYYY-MM-DD").toDate()
          : moment().startOf("day").toDate();

      const response = await servScalePriceService.getHistory({
        dateSinceInit: toDate(dateSinceInit),
        dateSinceEnd: toDate(dateSinceEnd),
        fields:
          fieldFilter && servScaleHistoryFieldKeys.includes(fieldFilter)
            ? [fieldFilter]
            : undefined,
        limit: pageSize,
        page,
      });

      if (response?.status !== 200) {
        notify(
          response?.message || "No se pudo cargar el historial de cambios",
          "error"
        );
        setHistoryRecords([]);
        setTotal(0);
        return;
      }

      const body = response.data;
      const paged = body?.data;
      if (
        paged &&
        typeof paged === "object" &&
        !Array.isArray(paged) &&
        Array.isArray(paged.data)
      ) {
        setHistoryRecords(paged.data);
        setTotal(Number(paged.total) || 0);
        return;
      }
      if (Array.isArray(body?.data)) {
        setHistoryRecords(body.data);
        setTotal(body.data.length);
        return;
      }
      setHistoryRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fieldFilter, dateSinceInit, dateSinceEnd, page, pageSize]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const bumpPage = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const fieldOptions = useMemo(
    () =>
      servScaleHistoryFieldKeys.map((key) => ({
        value: key,
        label: SERV_SCALE_FIELD_LABELS[key] ?? key,
      })),
    []
  );

  const hasRows = historyRecords.some((rec) =>
    Array.isArray(rec.changes)
      ? rec.changes.some(
          (ch) => ch?.field && (!fieldFilter || ch.field === fieldFilter)
        )
      : false
  );

  return (
    <div className="baremo-history">
      <div className="baremo-history__filters">
        <label className="baremo-history__filter">
          <span className="baremo-history__filter-label">Variable</span>
          <select
            className="baremo-history__select"
            value={fieldFilter}
            onChange={bumpPage(setFieldFilter)}
            aria-label="Filtrar por variable"
          >
            <option value="">Todas las variables</option>
            {fieldOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="baremo-history__filter">
          <span className="baremo-history__filter-label">Fecha desde</span>
          <input
            type="date"
            className="baremo-history__date"
            value={dateSinceInit}
            onChange={bumpPage(setDateSinceInit)}
            aria-label="Fecha de aplicación desde"
          />
        </label>
        <label className="baremo-history__filter">
          <span className="baremo-history__filter-label">Fecha hasta</span>
          <input
            type="date"
            className="baremo-history__date"
            value={dateSinceEnd}
            onChange={bumpPage(setDateSinceEnd)}
            aria-label="Fecha de aplicación hasta"
          />
        </label>
        <label className="baremo-history__filter baremo-history__filter--page-size">
          <span className="baremo-history__filter-label">Por página</span>
          <select
            className="baremo-history__select baremo-history__select--sm"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Resultados por página"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="baremo-history__loading">Cargando historial…</p>
      ) : !hasRows ? (
        <p className="baremo-history__empty parameters-page__muted">
          No hay registros para los filtros seleccionados.
        </p>
      ) : (
        <>
          <HistoryPager
            total={total}
            page={page}
            totalPages={totalPages}
            onPrev={goPrev}
            onNext={goNext}
            onGoTo={goTo}
          />

          <div className="baremo-history__table-wrap">
            <table className="baremo-history__table">
              <thead>
                <tr>
                  {servScalePriceHistoryTableHeader.map((col) => (
                    <th key={col.value}>{col.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historyRecords.flatMap((rec) => {
                  const changes = rec.changes || [];
                  const appliedAt = rec.recordDateSince ?? rec.createdAt;
                  const appliedAtMoment = moment(appliedAt);
                  const name = userDisplayName(rec.createdBy);
                  const initials = userInitials(name, "");
                  const bg = avatarBgForName(name);
                  return changes
                    .filter(
                      (ch) =>
                        ch?.field && (!fieldFilter || ch.field === fieldFilter)
                    )
                    .map((ch, i) => {
                      const label =
                        SERV_SCALE_FIELD_LABELS[ch.field] ?? ch.field ?? "—";
                      return (
                        <tr key={`${rec._id ?? "r"}-${ch.field}-${i}`}>
                          <td>{label}</td>
                          <td>
                            {appliedAt == null || appliedAt === ""
                              ? "—"
                              : appliedAtMoment.isValid()
                                ? appliedAtMoment.format("DD/MM/YYYY")
                                : String(appliedAt)}
                          </td>
                          <td>
                            <span className="baremo-history__pill baremo-history__pill--prev">
                              {formatHistoryValue(ch.field, ch.from)}
                            </span>
                          </td>
                          <td>
                            <span className="baremo-history__pill baremo-history__pill--new">
                              {formatHistoryValue(ch.field, ch.to)}
                            </span>
                          </td>
                          <td>
                            <div className="baremo-history__user">
                              <span
                                className="baremo-history__avatar"
                                style={{ backgroundColor: bg }}
                                aria-hidden
                              >
                                {initials}
                              </span>
                              <span className="baremo-history__user-name">
                                {name}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                })}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <HistoryPager
              isFooter
              total={total}
              page={page}
              totalPages={totalPages}
              onPrev={goPrev}
              onNext={goNext}
              onGoTo={goTo}
            />
          )}
        </>
      )}
    </div>
  );
};
